import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { FaCheck } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";
import FromLayout from "../../../component/FromLayout";
import FeeSelector from "../../../component/selectbox/FeeSelector";
import { useRegisterParentMutation } from "../../../store/api/parent/registrationSlice";
import {
  capitalizeString,
  handleApiError,
} from "../../../utils/dateAndTimeConversion";
import { useTranslation } from "react-i18next";
import { FaMapMarkerAlt } from "react-icons/fa";
import {
  logInUser,
  logOutTempUser,
  storeTempData,
} from "../../../store/reducer/UserReducer";

const initialStudent = {
  full_name: "",
  street: "",
  city: "",
  state: "",
  country: "",
  zip_code: "",
  latitude: "",
  longitude: "",
  is_disabled: false,
  disability_notes: "",
  dr_phone_no: "",
  // school_id: "",
  // vendor_id: "",
  bus_fee_id: "",
  //
  sameAsParent: false,
};

const initialState = {
  full_name: "",
  phone: "",
  street: "",
  city: "",
  state: "",
  country: "",
  zip_code: "",
  latitude: "",
  longitude: "",
  email: "",
  students: [initialStudent],
};

// ------------------------------------
// Google Places Component
// ------------------------------------
export const AddressAutocomplete = ({
  value,
  onChange,
  onSelect,
  disabled,
  name = "street",
}) => {
  const { t } = useTranslation();
  const {
    ready,
    value: inputValue,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    debounce: 500,
    defaultValue: value,
  });
  // 🔥 FIX: Sync external value with internal autocomplete value
  useEffect(() => {
    if (value !== inputValue) {
      setValue(value || "", false); // false = do not trigger new suggestions
    }
  }, [value, inputValue, setValue]);

  // Input handler
  const handleInput = (e) => {
    setValue(e.target.value);
    onChange(e);
  };

  // When user selects suggestion
  const handleSelect = async (description) => {
    setValue(description, false);
    clearSuggestions();

    const geocode = await getGeocode({ address: description });
    const { lat, lng } = await getLatLng(geocode[0]);
    const address = geocode[0].address_components;

    const getComponent = (type) =>
      address.find((c) => c.types.includes(type))?.long_name || "";

    onSelect({
      street: description,
      city: getComponent("locality") || getComponent("sublocality"),
      state: getComponent("administrative_area_level_1"),
      country: getComponent("country"),
      zip_code: getComponent("postal_code"),
      latitude: lat,
      longitude: lng,
    });
  };

  // 🆕 Get Current Location
  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error(t("geolocation_not_supported"));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;

        const geocode = await getGeocode({ location: { lat, lng } });
        const description = geocode[0].formatted_address;
        const address = geocode[0].address_components;

        const getComponent = (type) =>
          address.find((c) => c.types.includes(type))?.long_name || "";

        // Set input & send to parent
        setValue(description, false);

        onSelect({
          street: description,
          city: getComponent("locality") || getComponent("sublocality"),
          state: getComponent("administrative_area_level_1"),
          country: getComponent("country"),
          zip_code: getComponent("postal_code"),
          latitude: lat,
          longitude: lng,
        });
      },
      () =>
        toast.error(t("location_permission_error")),
    );
  };

  return (
    <div style={{ position: "relative" }}>
      <div className="d-flex gap-2">
        {/* Input */}
        <input
          className="form-control address"
          disabled={disabled}
          value={inputValue}
          onChange={handleInput}
          name={name}
          placeholder={` ${t("search")} ${t("your")} ${t("address")}...`}
        />

        {/* 🆕 Current Location Button */}
        {name !== "destination_address" && (
          <button
            type="button"
            className="btn btn-dark"
            onClick={handleUseCurrentLocation}
            disabled={disabled}
            style={{ whiteSpace: "nowrap" }}
          >
            <FaMapMarkerAlt />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {status === "OK" && !disabled && (
        <ul
          className="list-group position-absolute w-100 mt-1 shadow"
          style={{ zIndex: 9999 }}
        >
          {data.map(({ place_id, description }) => (
            <li
              key={place_id}
              className="list-group-item list-group-item-action"
              onClick={() => handleSelect(description)}
              style={{ cursor: "pointer" }}
            >
              {description}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ------------------------------------
// Main Form Component
// ------------------------------------

const SignUp = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { tempData } = useSelector((state) => state.UserReducer);
  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [feeErrors, setFeeErrors] = useState({});

  const [registerParent, { isLoading }] = useRegisterParentMutation();

  // Parent simple inputs
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Parent Address Autocomplete
  const handleParentAddressSelect = (address) => {
    setFormData((prev) => ({
      ...prev,
      ...address,
    }));
  };

  // Student field update
  const handleChangeStudent = (index, field, value) => {
    setFormData((prev) => {
      const arr = [...prev.students];

      // 🔥 If user checked "sameAsParent"
      if (field === "sameAsParent" && value === true) {
        arr[index] = {
          ...arr[index],
          sameAsParent: true,
          street: prev.street,
          city: prev.city,
          state: prev.state,
          country: prev.country,
          zip_code: prev.zip_code,
          latitude: prev.latitude,
          longitude: prev.longitude,
        };
      } else {
        // normal update
        arr[index] = { ...arr[index], [field]: value };
      }

      return { ...prev, students: arr };
    });
  };

  // Student autocomplete select
  const handleStudentAddressSelect = (index, address) => {
    setFormData((prev) => {
      const arr = [...prev.students];
      arr[index] = { ...arr[index], ...address };
      return { ...prev, students: arr };
    });
  };

  // Add a new student
  const addStudent = () => {
    const { createdByDetails } = tempData || {};

    // const roleObj =
    //   createdByDetails?.role === "vendor"
    //     ? { vendor_id: createdByDetails?.id || "" }
    //     : { school_id: createdByDetails?.id || "" };

    setFormData((prev) => ({
      ...prev,
      students: [...prev.students, initialStudent],
    }));
  };

  // Remove a student
  const removeStudent = (idx) => {
    setFormData((prev) => ({
      ...prev,
      students: prev.students.filter((_, i) => i !== idx),
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate parent fields
    const requiredParentFields = ["full_name", "phone", "street"];

    requiredParentFields.forEach((errors) => {
      if (!formData[errors] || formData[errors].trim() === "") {
        newErrors[errors] = `${capitalizeString(errors)} is required`;
      }
    });

    // Validate email format
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t("email_invalid");
    }

    // Validate phone format (basic validation)
    if (formData.phone && !/^[\d\s\-\+\(\)]{10,}$/.test(formData.phone)) {
      newErrors.phone = t("phone_invalid");
    }

    // Validate students
    formData.students.forEach((student, index) => {
      if (!student.full_name || student.full_name.trim() === "") {
        newErrors[`student_${index}_name`] = t("student_name_required");
      }

      if (!student.bus_fee_id) {
        newErrors[`student_${index}_fee`] = t("fee_required");
      }

      // If student has special needs, validate required fields
      if (student.is_disabled) {
        if (!student.dr_phone_no || student.dr_phone_no.trim() === "") {
          newErrors[`student_${index}_dr_phone`] = t("dr_phone_required");
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (Object.values(feeErrors).some(Boolean)) {
      toast.error(t("correct_fee_type"));
      return;
    }
    toast.dismiss();
    try {
      const response = await registerParent(formData).unwrap();
      if (response.statusCode === 200) {
        toast.success(response.data.message);
        if (response.data.redirect_to_payment) {
          dispatch(storeTempData({ ...tempData, status: "payment_pending" }));
          navigate("/payment");
        } else {
          dispatch(logOutTempUser());
          // dispatch(logInUser({ ...tempData, status: "active" }));
          navigate("/");
        }
      }
    } catch (error) {
      handleApiError(error, t);
    }
  };

  useEffect(() => {
    if (tempData) {
      const { email, status } = tempData;
      if (status === "payment_pending") {
        navigate("/payment");
      }

      setFormData((prev) => ({
        ...prev,
        email,
      }));
    } else {
      navigate("/");
    }
  }, [tempData]);

  return (
    <FromLayout title={`${t("parent")} & ${t("student")} ${t("registration")}`}>
      <form onSubmit={handleSubmit}>
        <div className="text-center mb-5">
          <p
            className="text-light"
            style={{ opacity: 0.8, maxWidth: "500px", margin: "0 auto" }}
          >
            {t("parent_guardian_info_note")}
          </p>
        </div>

        {/* Parent Details */}
        <h5 className="fw-semibold text-white text-start mb-3">
          {t("parent")} {t("details")}
        </h5>
        <div className="row g-3 text-start">
          <div className="col-md-6 col-lg-4 col-12">
            <label className="form-label">
              {t("full")} {t("name")} *
            </label>
            <input
              className="form-control"
              value={formData.full_name}
              name="full_name"
              onChange={handleChange}
              required
            />
            {errors?.full_name && (
              <span className="text-danger small">{errors.full_name}</span>
            )}
          </div>

          <div className="col-md-6 col-lg-4 col-12">
            <label className="form-label">
              {t("email")} {t("address")} *
            </label>
            <input
              type="email"
              className="form-control"
              value={formData.email}
              name="email"
              onChange={handleChange}
              readOnly
              required
            />
            {errors?.email && (
              <span className="text-danger small">{errors.email}</span>
            )}
          </div>

          <div className="col-md-6 col-lg-4 col-12">
            <label className="form-label">{t("phone")} *</label>
            <input
              type="tel"
              className="form-control"
              value={formData.phone}
              name="phone"
              onChange={handleChange}
              required
            />
            {errors?.phone && (
              <span className="text-danger small">{errors.phone}</span>
            )}
          </div>

          {/* Parent Address Autocomplete */}
          <div className="col-12">
            <label className="form-label">{t("address")} *</label>
            <AddressAutocomplete
              value={formData.street}
              onChange={handleChange}
              onSelect={handleParentAddressSelect}
            />

            {errors?.street && (
              <span className="text-danger small">{errors.street}</span>
            )}
          </div>
        </div>

        {/* Student Section */}
        <div className="d-flex align-items-center justify-content-between mt-5 mb-2 text-start">
          <h5 className="fw-semibold text-white mb-0">
            {t("student")} {t("details")}
          </h5>
          <button
            type="button"
            className="btn btn-dark px-4 rounded-3"
            onClick={addStudent}
          >
            + {t("add")} {t("student")}
          </button>
        </div>

        {formData.students.map((student, idx) => (
          <div key={idx} className="border rounded-4 p-4 mt-3 text-start">
            <div className="row g-3">
              {/* Student Name */}
              <div className="col-md-6 col-12">
                <label className="form-label">
                  {t("student")} {t("name")} *
                </label>
                <input
                  className="form-control"
                  value={student.full_name}
                  onChange={(e) =>
                    handleChangeStudent(idx, "full_name", e.target.value)
                  }
                  required
                />
                {errors[`student_${idx}_name`] && (
                  <span className="text-danger small">
                    {errors[`student_${idx}_name`]}
                  </span>
                )}
              </div>
              {/* Fee / Service Type */}
              <div className="col-md-6 col-12">
                <label className="form-label">
                  {t("student")} {t("fee")} *
                  {tempData?.is_private === 1 && (
                    <span
                      className="ms-2 px-2 py-0 rounded"
                      style={{
                        fontSize: "0.72rem",
                        fontWeight: 600,
                        background: "rgba(25,135,84,0.12)",
                        color: "#198754",
                        border: "1px solid rgba(25,135,84,0.3)",
                      }}
                    >
                      Free
                    </span>
                  )}
                </label>
                <FeeSelector
                  bus_fee_id={student.bus_fee_id}
                  change={(name, value) =>
                    handleChangeStudent(idx, name, value)
                  }
                  isPrivate={tempData?.is_private}
                  studentIndex={idx}
                  darkTheme
                  onFeeError={(hasError) =>
                    setFeeErrors((prev) => ({ ...prev, [idx]: hasError }))
                  }
                />
                {errors[`student_${idx}_fee`] && (
                  <span className="text-danger small">
                    {errors[`student_${idx}_fee`]}
                  </span>
                )}
              </div>

              {/* Address with Same as Parent Checkbox */}
              <div className="col-12">
                <label className="form-label d-flex align-items-center gap-3">
                  <span>{t("address")}</span>
                  <Form.Check
                    type="checkbox"
                    checked={student.sameAsParent}
                    onChange={(e) =>
                      handleChangeStudent(idx, "sameAsParent", e.target.checked)
                    }
                    label={` ${t("same")} ${t("as")} ${t("parent")}`}
                  />
                </label>

                <AddressAutocomplete
                  value={student.street}
                  disabled={student.sameAsParent}
                  onSelect={(addr) => handleStudentAddressSelect(idx, addr)}
                  onChange={(e) =>
                    handleChangeStudent(idx, "street", e.target.value)
                  }
                />
              </div>

              {/* Medical Conditions Checkbox */}
              <div className="col-12 pt-2">
                <div className="d-flex align-items-center">
                  <Form.Check
                    type="checkbox"
                    id={`is_disabled_${idx}`}
                    className="me-2"
                    checked={student.is_disabled}
                    onChange={(e) =>
                      handleChangeStudent(idx, "is_disabled", e.target.checked)
                    }
                  />
                  <label
                    htmlFor={`is_disabled_${idx}`}
                    className="text-white mb-0"
                    style={{ opacity: 0.9, cursor: "pointer" }}
                  >
                    {t("child_special_conditions")}
                  </label>
                </div>
              </div>

              {/* Medical Details (shown if checked) */}
              {student.is_disabled && (
                <div className="col-12 pt-3 border-top">
                  <div className="row g-3">
                    <div className="col-md-6 col-12">
                      <label className="form-label">
                        {t("Doctor's")} {t("phone")} {t("number")} *
                      </label>
                      <input
                        className="form-control"
                        value={student.dr_phone_no}
                        onChange={(e) =>
                          handleChangeStudent(
                            idx,
                            "dr_phone_no",
                            e.target.value,
                          )
                        }
                        required
                      />
                      {errors[`student_${idx}_dr_phone`] && (
                        <span className="text-danger small">
                          {errors[`student_${idx}_dr_phone`]}
                        </span>
                      )}
                    </div>
                    <div className="col-md-6 col-12">
                      <label className="form-label">
                        {t("disability")} {t("notes")}
                      </label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={student.disability_notes}
                        onChange={(e) =>
                          handleChangeStudent(
                            idx,
                            "disability_notes",
                            e.target.value,
                          )
                        }
                        placeholder={`${t("describe_medical_conditions")}...`}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Delete Button */}
              <div className="col-12 d-flex justify-content-end">
                {formData.students.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-dark px-4 rounded-3 shadow-sm"
                    onClick={() => removeStudent(idx)}
                    aria-label={`Delete student ${idx + 1}`}
                  >
                    {t("btns.delete")} {t("student")}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Important Section */}
        <div className="important-box mt-5 p-4 border rounded-4 text-white text-start">
          <div className="important-title mb-3">
            {t("important")} – {t("please")} {t("read")}
          </div>
          <div className="important-list small">
            <div className="d-flex align-items-start gap-3">
              <FaCheck className="text-white" size={20} />
              <p>{t("bus_program_commitment_note")}</p>
            </div>

            <div className="d-flex align-items-start gap-3">
              <FaCheck className="text-white" size={20} />
              <p>{t("bus_guidelines_acknowledgement")}</p>
            </div>

            <div className="d-flex align-items-start gap-3">
              <FaCheck className="text-white" size={20} />
              <p>
                {t("bus_payment_notice")}{" "}
                <strong>{t("bus_payment_deadline")}</strong>{" "}
                {t("for_busing_year")}
              </p>
            </div>
          </div>
        </div>

        <div className="d-flex flex-column flex-md-row gap-3 justify-content-between mt-5">
          <button
            onClick={handleSubmit}
            type="button"
            className="btn btn-amber rounded-3 px-4 ms-auto"
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="spinner-border" role="status">
                <span className="sr-only p-0"></span>
              </div>
            ) : (
              t("btns.submit")
            )}
          </button>
        </div>
      </form>
    </FromLayout>
  );
};

export default SignUp;
